import React, {FunctionComponent,useState,useRef} from "react";
import { connect, useDispatch } from "react-redux";
import {addNoteFirebase} from "../../../Services/contacts";
import Loading from "../../Loading/Loading";
import { showSnackbarAction } from "../../../Redux/actions";
import { useHistory } from "react-router-dom";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import { styled } from "@mui/material/styles";
import { TextField } from "@mui/material";

const primaryColor = "#292744";

/**
 * Custom TextArea using Material-UI styles
 * Provides rounded appearance, consistent theming, hover/focus border styling.
 */
const CustomTextArea = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    fontSize: "var(--font-size-large)",
    fontFamily: "var(--font-family-main)",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`
    },
  },
  "& .MuiInputBase-input": {
    padding: "14px",
    fontSize: 16,
    color: "#222",
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    fontSize: 15,
    color: primaryColor,
  },
});

/** Props definition for NotesModal */
type props = {
  /** Existing notes list for the contact */
  notes: any[];

  /** Complete contact object (contains contactId) */
  contactDetail: any;

  /** Closes the modal */
  close: () => void;

  /** Controls modal open/close state */
  open: boolean;

  /** Email of user adding the note (optional) */
  userEmail?: string;

  /** Boolean controlling theme (light/dark) */
  theme: any;
};

/**
 * NotesModal Component
 * ----------------------------------------------------------
 * Modal window used to add a **new note** to a contact.
 *
 * Features:
 *  - Validates note input
 *  - Adds note using Firebase service
 *  - Displays a loader while saving
 *  - Uses styled MUI textarea + custom buttons
 *  - Mobile responsive layout
 */
const NotesModal: FunctionComponent<props> = ({
  notes,
  contactDetail,
  close,
  open,
  userEmail,
  theme
}) => {
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /** Loader state while saving */
  const [load, setLoad] = useState(false);

  /** Stores note text (duplicate of ref but used for re-render) */
  const [note, setNote] = useState("");

  /** Ref for capturing TextArea value */
  const noteRef: any = useRef();

  const dispatcher = useDispatch();

   /**
   * Handles submitting a new note
   * --------------------------------
   * Validates input → Calls Firebase → Closes modal on success
   */
  const onSubmit = () => {
    if (noteRef.current.value === "") {
      dispatcher(
        showSnackbarAction("Please Add Note!!", "error")
      );
    } else {
      addNoteFirebase(
        contactDetail.contactId,
        notes,
        noteRef.current.value,
        (data) => setLoad(data),
        dispatcher,
        close,
        userEmail
      );
    }
  };

   /**
   * Modal styling for react-modal
   * Adapts dynamically to theme and screen size
   */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", // darkens the background
      zIndex: 1300, // higher than MUI AppBar/Table
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      maxWidth: 380,
      minWidth: 320,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        {load && <Loading />}
        <Box
          sx={{
            width: "100%",
            maxWidth: 340,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
           {/* Form Container */}
          <form style={{ width: "100%" }}>

             {/* Title */}
            <Box sx={{ mb: 2 }}>
              <h3
                style={{
                  color: theme ? "#272944" : "#fff",
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-large)",
                  marginBottom: 24,
                  textAlign: "center",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                Create New Note
              </h3>
            </Box>

            {/* Input Area */}
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid item xs={12}>
                <CustomTextArea
                  fullWidth
                  label="Notes"
                  inputRef={noteRef}
                  variant="outlined"
                  multiline
                  rows={1}
                  onChange={(e) => setNote(e.target.value)}
                  value={note}
                  InputLabelProps={{ style: { color: "#555", fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-medium)" } }}
                  InputProps={{ style: { color: '#222', fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-medium)" } }}
                />
              </Grid>
            </Grid>

            {/* Footer Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  width: "100%",
                }}
              >
                
                 {/* Cancel Button */}
                <Box style={{ marginRight: 8 }}>
                  <StyledCancelButton onClick={close} isMobile={isMobile}>
                    Cancel
                  </StyledCancelButton>
                </Box>

                 {/* Submit Button */}
                <Box>
                  <StyledSubmitButton
                    onClick={onSubmit}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    {load ? (
                      <span style={{ display: "flex", justifyContent: "center" }}>
                        <Circles color="#fff" height={20} width={20} />
                      </span>
                    ) : (
                      <>Submit</>
                    )}
                  </StyledSubmitButton>
                </Box>
              </Box>
            </Grid>
          </form>
        </Box>
      </Modal>
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(NotesModal);